import { flatten, map, pathOr, find, propEq, filter, prop, uniqBy, equals } from 'ramda';
import { RolePermission } from 'src/types/Permissions';
import { LOAN_PERMISSIONS_AND_ACTIVITY } from 'src/queries/useQuery';
import { APPLICANT_ACCESS_ON_BEHALF_CO_APPLICANT_WRITE } from './Rules/permissionData';
import { isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { useQueryClient } from 'react-query';
import { filterResourcesByAccountId } from 'src/services/objects/finder';
import {
  useAccountRolePermissions
} from 'src/hooks/usePermission';
import { VarMapsHandler } from '../Advisors/Settings/Global/VarMaps/VarMaps';
import { getVarMapNames } from '../Advisors/Settings/ManageAccess/Permissions/Permissions';
import { getNamespaceName } from 'src/services/objects/namespaces';


export const flattenPermissions = (roles: any) => {
  return flatten(map((response: any) =>
    map((item: any) => {
      return {
        name: pathOr('', ['name'], item),
        options: pathOr({}, ['options'], item)
      }
    }, pathOr([], ['permissions'], response)), roles));
}

export const getPermissionByRoles = (accountRoles: any, accountId?: string) => {
  // TODO: local products pull should support document access control pull using local file
  const { data, isLoading = true } = useAccountRolePermissions(accountRoles, accountId)
  
  const rolesPermissions = pathOr([], ['roles'], data);
  const permissions: any = flattenPermissions(rolesPermissions);
  const loanProducts = pathOr([], ['loan_products'], data);
  const podConfigs = pathOr([], ['pod_configs'], data);
  const documentAccessControls = pathOr([], ['document_access_controls'], data);
  const globalPermissions = pathOr([], ['global_permission_templates'], data);
  const miniGlobalDocumentArtifacts = pathOr([], ['global_document_artifacts'], data);
  const branches = pathOr([], ['branches'], data);
  const teams = pathOr([], ['teams'], data);
  const varmaps = pathOr([], ['varmaps'], data);


  return { 
    permissions,
    rolesPermissions,
    globalPermissions,
    miniGlobalDocumentArtifacts,
    loanProducts, 
    podConfigs,
    documentAccessControls, 
    isLoading,
    branches,
    teams,
    varmaps
  };
}

/**
getPermissionByName is used to find permission which match with given name
@param name { string } permission_name to be matched with list
@param permissions { list } permissions array
return permission which matches with given name or else return undefined
*/
export const findPermissionByName = (name: string, permissions: RolePermission[]) => {
  return find(propEq('name', name), permissions || [])
}


/**
hasConsentYesToCoBorrower used to check is borrower has permission to access coborrowers
application flow questions, tasks, documents
@param loanAppId { string } this loan application id to fetch react query cache
*/

export const hasConsentYesToCoBorrower = (loanAppId: string, accountId: string) => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(
    [LOAN_PERMISSIONS_AND_ACTIVITY, loanAppId]
  );
  const loanPermissions = pathOr([], ['loan_permissions'], data)
  const shouldDescribeAllLoanRole = isNotEmpty(findPermissionByName(
    APPLICANT_ACCESS_ON_BEHALF_CO_APPLICANT_WRITE,
    filterResourcesByAccountId(loanPermissions, accountId)
  ))
  return shouldDescribeAllLoanRole
}


/**
 * Determines if a specific pipeline should have its limit disabled based on a given configuration.
 *
 * @param pipelineName - The name of the pipeline to check.
 * @param pipelinesToDisable - An array of pipeline configurations, where each configuration contains a "value" property representing the pipeline name.
 *
 * @returns A boolean indicating whether the limit for the specified pipeline should be disabled.
 *
 * @example
 * ```typescript
 * const pipelineName = "my_pipeline";
 * const pipelinesToDisable = [
 *   { label: "My Pipeline" },
 *   { value: "my_pipeline" },
 * ];
 *
 * const shouldDisableLimit = shouldDisableLimitBasedOnConfig(pipelineName, pipelinesToDisable);
 * console.log(shouldDisableLimit); // Output: true
 * ```
 */
export const shouldDisableLimitBasedOnConfig = (pipelineName: string, pipelinesToDisable: any[]) => {
  return isNotEmpty(filter((tab: any) => equals(pipelineName, pathOr("", ["value"], tab)), pipelinesToDisable))
}



/**
 * 
 * @param permissions Contains all the perission for current role
 * @param permissionName permission name to be filtered 
 * @returns  if there are multiple same permissions it will combine both the permissions multioptions
 */

export const getMultiplePermissionOptions = (
  permissions: any[],
  permissionName: string,
  varmaps: any = [],
) => {
  const prms = filter(
    propEq('name', permissionName),
    permissions
  );
  const options = uniqBy(prop('value'), flatten(map((prm: any) => pathOr([], ["options", "multi_option"], prm), prms)));
  const varMapOptions = new VarMapsHandler(varmaps).getVarMapConfig(getVarMapNames(permissionName, getNamespaceName()), 'multi_option')
  //here we are combining multiple permission option into single one
  return returnIsNotEmptyOrTrue(
    options,
    options,
    isNotEmpty(prms) ? varMapOptions : []  //return the varmap option only if the permission is configured for the current role
  )
}
